import { useMemo } from "react";
import { RegisterOptions } from "react-hook-form";
import * as Sentry from "@sentry/react";

import { FieldConfig } from "components/Form/types";

/**
 * Builds the react-hook-form validation rules shared by the free-text field
 * components (short and long text). Compiles `pattern` (the value must match)
 * and `disallowed_patterns` (case-insensitive blocklist the value must not
 * match) once; an uncompilable pattern is reported to Sentry and skipped
 * rather than blocking input.
 */
export function useTextFieldRules(field: FieldConfig): RegisterOptions {
  const patternRule = useMemo(() => {
    if (!field.validations?.pattern) return undefined;
    try {
      return {
        value: new RegExp(field.validations.pattern),
        message:
          field.validations.pattern_message || "Please enter a valid value.",
      };
    } catch {
      Sentry.captureException(
        new Error(`Invalid regex pattern: ${field.validations.pattern}`),
      );
      return undefined;
    }
  }, [field.validations?.pattern, field.validations?.pattern_message]);

  const disallowedRules = useMemo(() => {
    return (field.validations?.disallowed_patterns ?? []).flatMap(
      ({ pattern, message }) => {
        try {
          return [
            {
              value: new RegExp(pattern, "i"),
              message: message || "Please enter a valid value.",
            },
          ];
        } catch {
          Sentry.captureException(
            new Error(`Invalid disallowed_pattern: ${pattern}`),
          );
          return [];
        }
      },
    );
  }, [field.validations?.disallowed_patterns]);

  return {
    required: field.validations?.required ? "This field is required." : false,
    minLength: field.validations?.min_length
      ? {
          value: field.validations.min_length,
          message: `Must be at least ${field.validations.min_length} characters.`,
        }
      : undefined,
    maxLength: field.validations?.max_length
      ? {
          value: field.validations.max_length,
          message: `Must be no more than ${field.validations.max_length} characters.`,
        }
      : undefined,
    pattern: patternRule,
    validate: {
      notOnlyWhitespace: (value) => {
        if (field.validations?.required && value?.trim() === "") {
          return "This field is required.";
        }
        return true;
      },
      notDisallowed: (value) => {
        if (typeof value !== "string" || value === "") {
          return true;
        }
        const match = disallowedRules.find((rule) => rule.value.test(value));
        return match ? match.message : true;
      },
    },
  };
}
